import { type FC, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { useFilter } from "@/partner/shared/components/Filter";
import { useCountryAccessesSelectQuery } from "@/partner/entities/countryAccesses";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  ProductTypeType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import {
  buildProductsFilter,
  DEFAULT_COUNTRY_ACCESS_VALUE,
} from "../model/buildProductsFilter";
import { ProductTypeField } from "./ProductTypeField";
import {
  ProductStatusField,
  type ProductStatusFieldValue,
} from "./ProductStatusField";

// Спец-значение «продукты без явного шаблона гео-доступа» (référence:
// ProductsInner.js — countryAccessId==="DEFAULT" маппится в null на бэкенде,
// в отличие от «фильтр не задан», где параметр вовсе отсутствует в URL).

interface ProductsFilterProps {
  onCreate: () => void;
}

export const ProductsFilter: FC<ProductsFilterProps> = ({ onCreate }) => {
  const { t } = useTranslation();
  // référence: partner/hooks/useIsMobile("md") — тот же breakpoints.down("md"),
  // но наш shared/hooks/useIsMobile принимает media-query СТРОКУ, а не имя
  // брейкпоинта: useIsMobile("md") молча резолвился в isMobile===false
  // всегда (см. отчёт аудита, п.1). useDeviceBreakpoint — точный порт
  // референсного хука (уже используется в ProductsListItem).
  const isMobile = useDeviceBreakpoint("md");
  const { filter, onChangeFilter, onClearFilter } = useFilter();

  const { data: countryAccessesData } = useCountryAccessesSelectQuery();
  const countryAccessTemplates =
    countryAccessesData?.countryAccesses?.nodes ?? [];
  const countryAccessOptions = [
    {
      value: DEFAULT_COUNTRY_ACCESS_VALUE,
      label: t("products.filter.countryAccessDefaultOption"),
    },
    ...countryAccessTemplates.map((item) => ({
      value: item.id,
      label: item.title,
    })),
  ];
  const selectedCountryAccess =
    countryAccessOptions.find(
      (option) => option.value === filter.countryAccessId
    ) ?? null;

  const selectedTypes: ProductTypeType[] = filter.types ?? [];
  const selectedStatus: ProductStatusFieldValue = filter.status ?? null;

  const [opened, setOpened] = useState(false);

  // Локальный стейт для дебаунса поиска — не дёргаем onChangeFilter (навигация
  // по URL) на каждое нажатие клавиши, а только через 400мс простоя
  const [searchInput, setSearchInput] = useState(filter.search || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      onChangeFilter("search", searchInput);
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleClearSearch = () => {
    setSearchInput("");
    onChangeFilter("search", "");
  };

  const handleClearFilter = () => {
    setSearchInput("");
    onClearFilter();
  };

  const onClickToggleFilters = () => {
    setOpened((v) => !v);
  };

  const isFieldActive = (field: string): boolean => {
    switch (field) {
      case "search":
        return Boolean(filter.search);
      case "countryAccessId":
        return Boolean(filter.countryAccessId);
      case "dateFrom":
        return Boolean(filter.dateFrom);
      case "dateTo":
        return Boolean(filter.dateTo);
      case "status":
        return Boolean(filter.status);
      case "types":
        return selectedTypes.length > 0;
      default:
        return false;
    }
  };

  const isVisible = (field: string): boolean => opened || isFieldActive(field);

  const anyVisible = opened || Object.keys(filter).length > 0;
  const showClearButton = Object.keys(filter).length > 0 && !isMobile;

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={isMobile ? 1 : 2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">{t("products.pageTitle")}</Typography>
            <Box style={{ marginLeft: "auto" }}>
              <Stack spacing={1} direction="row">
                {showClearButton && (
                  <Tooltip title={t("products.filter.resetTooltip")}>
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      onClick={handleClearFilter}
                      sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip
                  title={
                    opened
                      ? t("products.filter.closeFiltersTooltip")
                      : t("products.filter.openFiltersTooltip")
                  }
                >
                  <IconButton
                    size={isMobile ? "small" : "medium"}
                    onClick={onClickToggleFilters}
                    sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                  >
                    {opened ? <CloseIcon /> : <FilterListIcon />}
                  </IconButton>
                </Tooltip>

                <DownloadReportButton
                  type={ReportTypeType.ProductsType}
                  filter={
                    buildProductsFilter(filter) as
                      | Record<string, unknown>
                      | undefined
                  }
                />

                <Button variant="contained" disableElevation onClick={onCreate}>
                  {t("products.filter.addButton")}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {anyVisible && (
          <Box>
            <Grid container spacing={2} alignItems="center">
              {isVisible("search") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    placeholder={t("products.filter.searchLabel")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                      endAdornment: searchInput && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}

              {isVisible("dateFrom") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label={t("products.filter.dateFromLabel")}
                    value={filter.dateFrom ? moment(filter.dateFrom) : null}
                    onChange={(value) =>
                      onChangeFilter(
                        "dateFrom",
                        value ? value.toISOString() : ""
                      )
                    }
                    maxDate={filter.dateTo ? moment(filter.dateTo) : undefined}
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </Grid>
              )}

              {isVisible("dateTo") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label={t("products.filter.dateToLabel")}
                    value={filter.dateTo ? moment(filter.dateTo) : null}
                    onChange={(value) =>
                      onChangeFilter("dateTo", value ? value.toISOString() : "")
                    }
                    minDate={
                      filter.dateFrom ? moment(filter.dateFrom) : undefined
                    }
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </Grid>
              )}

              {isVisible("countryAccessId") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={countryAccessOptions}
                    value={selectedCountryAccess}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_e, value) =>
                      onChangeFilter("countryAccessId", value?.value ?? "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("products.filter.countryAccessLabel")}
                      />
                    )}
                  />
                </Grid>
              )}

              {isVisible("types") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProductTypeField
                    multiple
                    value={selectedTypes}
                    onChange={(value) => onChangeFilter("types", value)}
                    label={t("products.filter.typesLabel")}
                  />
                </Grid>
              )}

              {isVisible("status") && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <ProductStatusField
                    value={selectedStatus}
                    onChange={(value) => onChangeFilter("status", value ?? "")}
                    label={t("products.filter.statusLabel")}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
